"use client";

import { DATA_LAYER_EVENT_NAMES } from "@/constants/dataLayer";
import { useCurrency } from "@/contexts/CurrencyContext";
import { useModal } from "@/contexts/ModalContext";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import { useRouter } from "@/i18n/navigation";
import { commonService } from "@/services/commonService";
import { dateFormats } from "@/utils/dateFormatter";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { sendGTMEvent } from "@next/third-parties/google";
import clsx from "clsx";
import { useLocale } from "next-intl";
import { useCallback, useEffect, useId, useMemo, useState } from "react";
import Modal from "./ui/Modal";

// Constants
const PRODUCT_ROUTES = {
  Flight: "/flight",
  Hotel: "/hotel",
  Ferry: "/ferry",
  Car: "/car-rental",
  Bus: "/bus-ticket",
};

const PRODUCT_ICONS = {
  Flight: "new-flight",
  Hotel: "new-hotel",
  Ferry: "new-ferry",
  Car: "new-car",
  Bus: "new-bus",
};

const PRODUCT_TRANSLATIONS = {
  Flight: "header.flight",
  Hotel: "header.hotel",
  Ferry: "header.ferry",
  Car: "header.car",
  Bus: "header.bus",
  All: "common.all",
};

// Sub-components
const TicketDesign = ({ bgColor = "bg-white" }) => (
  <>
    <span className="absolute -top-[1px] -left-[10px] h-[10px] w-5 overflow-hidden">
      <div
        className={clsx(
          bgColor,
          "border-promo-subtle -mt-[9px] h-[18px] w-[18px] rounded-full border",
        )}
      />
    </span>
    <span className="absolute -bottom-[1px] -left-[10px] h-[10px] w-5 overflow-hidden">
      <div
        className={clsx(
          bgColor,
          "border-promo-subtle h-[18px] w-[18px] rounded-full border",
        )}
      />
    </span>
  </>
);

const ProductIcon = ({ type, className }) => {
  const iconName = PRODUCT_ICONS[type];
  if (!iconName) return null;

  return (
    <FontAwesomeIcon
      icon={byPrefixAndName.fak[iconName]}
      className={className}
    />
  );
};

const CampaignCard = ({ campaign, onClick }) => {
  const t = useTranslationDetector();

  const productName = t(
    PRODUCT_TRANSLATIONS[campaign.ProductType] || PRODUCT_TRANSLATIONS.All,
  );

  // Sol kısım için className - sol taraf normal rounded, sağ taraf içe dönük
  const iconContainerClasses = clsx(
    "border",
    "border-border-promo",
    "border-r-0",
    "flex",
    "h-full",
    "w-8",
    "lg:w-11",
    "items-center",
    "justify-center",
    "rounded-l-md",
    "bg-white",
  );

  // Sağ kısım için className - sol taraf içe dönük, sağ taraf normal rounded
  const contentContainerClasses = clsx(
    "border",
    "border-border-promo",
    "border-l-0",
    "absolute",
    "top-0",
    "left-8",
    "lg:left-11",
    "right-0",
    "h-full",
    "rounded-r-md",
    "bg-white",
    "z-2",
  );

  // Üst yuvarlak kesik için className
  const topCircleClasses = clsx(
    "border-border-promo",
    "absolute",
    "-top-[7px]",
    "left-[31px]",
    "lg:left-[43px]",
    "h-[14px]",
    "w-[14px]",
    "-translate-x-1/2",
    "rounded-full",
    "border-1",
    "z-10",
  );

  // Alt yuvarlak kesik için className
  const bottomCircleClasses = clsx(
    "border-border-promo",
    "absolute",
    "-bottom-[7px]",
    "left-[31px]",
    "lg:left-[43px]",
    "h-[14px]",
    "w-[14px]",
    "-translate-x-1/2",
    "rounded-full",
    "border-1",
    "z-10",
  );

  const dashLineClasses = clsx(
    "absolute",
    "top-[7px]",
    "left-[30.5px]",
    "md:left-[43px]",
    "md:h-[88%]",
    "h-[78%]",
    "w-[1px]",
    "dashed-line-vertical",
  );

  // Sağ tarafta dikey border efekti için className
  const rightBorderClasses = clsx(
    "absolute",
    "top-[6px]",
    "right-[-4px]",
    "h-[calc(100%-12px)]",
    "w-[6px]",
    "rounded-r-md",
    "bg-white",
    "z-1",
    "md:right-[-7px] md:w-[50px]",
  );

  return (
    <button
      className="flex cursor-pointer items-center justify-start"
      onClick={onClick}
      dir="ltr"
    >
      {/* Yeni ticket görünümü: dış button yapısı korunuyor */}
      <div className="relative h-[61px] w-[143px] md:h-[100px] md:w-[208px]">
        {/* Sol kısım - ürün ikonu */}
        <div
          className={iconContainerClasses}
          style={{
            clipPath:
              "polygon(0px 0px, 0px 100%, calc(100% - 8px) 100%, calc(100% - 5px) calc(100% - 6px), 100% calc(100% - 7px), 100% 7px, calc(100% - 5px) 6px, calc(100% - 8px) 0px)",
          }}
        >
          <ProductIcon
            type={campaign.ProductType}
            className="text-promo !h-[21px] !w-[21px] lg:!h-6 lg:!w-6"
          />
        </div>

        {/* Sol tarafta yuvarlak kesikler */}
        <div
          className={topCircleClasses}
          style={{ clipPath: "inset(50% 0 0 0)" }}
        />
        <div
          className={bottomCircleClasses}
          style={{ clipPath: "inset(0 0 50% 0)" }}
        />
        <div className={dashLineClasses} />

        {/* Sağ kısım - içerik */}
        <div
          className={contentContainerClasses}
          style={{
            clipPath:
              "polygon(8px 0px, 6px 0px, 5px 3px, 0 7px, 0 calc(100% - 7px), 5px calc(100% - 3px), 6px 100%, 8px 100%, 100% 100%, 100% 0)",
          }}
        >
          <div className="flex h-full items-center justify-start px-4">
            <div>
              <p className="text-purple text-left text-sm font-medium md:text-lg">
                {productName}
              </p>
              <p className="text-purple w-[85px] min-w-[85px] truncate text-left text-sm font-semibold md:w-[125px] md:text-lg">
                {campaign.DiscountText}
              </p>
            </div>
          </div>
        </div>

        {/* Sağ taraftaki dikey border efekti */}
        <div
          className={rightBorderClasses}
          style={{
            boxShadow: "inset -1px 0 0 0 var(--color-border-promo)",
          }}
        />
      </div>
    </button>
  );
};

const CampaignListItem = ({
  campaign,
  onDetailsClick,
  onApplyClick,
  currency,
}) => {
  const t = useTranslationDetector();

  /*
  sendGTMEvent({
    event: "custom_event",
    event_category: "coupon",
    event_action: DATA_LAYER_EVENT_NAMES.SELECT_COUPON,
    coupon_name: campaign?.CampaignCode,
    // coupon_value:    1000,
    coupon_currency: currency?.toUpperCase() || "TRY",
    coupon_category: campaign?.ProductType,
  });
  */

  const handleCampaignDetailsCtaClick = useCallback(() => {
    sendGTMEvent({
      event: "custom_event",
      event_category: "coupon",
      event_action: DATA_LAYER_EVENT_NAMES.SELECT_COUPON,
      coupon_name: campaign?.CampaignCode,
      coupon_value: null,
      coupon_currency: currency?.toUpperCase() || "TRY",
      coupon_category: campaign?.ProductType,
    });

    sendGTMEvent({
      event: "custom_event",
      event_category: "coupon",
      event_action: DATA_LAYER_EVENT_NAMES.VIEW_COUPON,
      coupon_name: campaign?.CampaignCode,
      coupon_value: null,
      coupon_currency: currency?.toUpperCase() || "TRY",
      coupon_category: campaign?.ProductType,
    });
  }, []);

  return (
    <div
      className="border-promo-subtle flex items-stretch gap-2 rounded-lg border bg-white pl-2"
      dir="ltr"
    >
      <ProductIcon
        type={campaign.ProductType}
        className="text-promo self-center text-[28px]"
      />
      <div className="border-promo-subtle relative border-l border-dashed">
        <TicketDesign bgColor="bg-background-global" />
      </div>
      <div className="flex w-full flex-col gap-3 p-4">
        <div className="flex flex-col gap-2">
          <div className="flex flex-col gap-1">
            <h3 className="text-lg font-semibold">{campaign?.DiscountText}</h3>
            <p className="font-medium">{campaign.Title}</p>
          </div>
          <p className="text-foreground-muted flex items-center gap-1 text-sm">
            <span>
              {t("common.expireDate", {
                0: dateFormats.withTime(campaign.EndDate),
              })}
            </span>
          </p>
        </div>
        <div className="flex items-center justify-between">
          <button
            className="text-promo cursor-pointer text-sm font-medium"
            onClick={() => {
              onDetailsClick?.();
              handleCampaignDetailsCtaClick();
            }}
          >
            {t("common.details")}
          </button>
          <button
            onClick={onApplyClick}
            className="bg-promo flex cursor-pointer items-center justify-center gap-2 rounded-lg px-4 py-2 font-medium text-white"
          >
            {t("common.apply")}
          </button>
        </div>
      </div>
    </div>
  );
};

const CampaignDetailsView = ({ campaign, onBack, currency }) => {
  const t = useTranslationDetector();
  console.log(campaign);
  return (
    <div className="flex flex-col p-4">
      <button
        onClick={onBack}
        className="text-promo mb-4 flex cursor-pointer items-center gap-2 font-medium"
      >
        <FontAwesomeIcon icon={byPrefixAndName.fas["arrow-left"]} />
        <span>{t("common.back")}</span>
      </button>

      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <h2 className="text-xl font-semibold">{campaign?.DiscountText}</h2>
          <h3 className="text-lg font-semibold">{campaign.Title}</h3>
          <p className="text-foreground-muted flex items-center gap-1 text-sm">
            <span>
              {t("common.expireDate", {
                0: dateFormats.withTime(campaign.EndDate),
              })}
            </span>
          </p>
        </div>

        {campaign.MainText && (
          <div
            className="bg-background-global scrollbar-promo max-h-[400px] overflow-y-auto rounded-lg p-4"
            dangerouslySetInnerHTML={{ __html: campaign.MainText }}
          />
        )}
      </div>
    </div>
  );
};

const ProductFilter = ({ productType, campaignCount, isSelected, onClick }) => {
  const t = useTranslationDetector();
  const productName = t(
    PRODUCT_TRANSLATIONS[productType] || PRODUCT_TRANSLATIONS.All,
  );

  return (
    <button
      className={clsx(
        "flex cursor-pointer items-center justify-between gap-1 rounded-lg border px-[10px] py-2 text-sm transition-all duration-300",
        isSelected
          ? "bg-primary-weaker border-primary text-primary"
          : "border-primary-weak",
      )}
      onClick={onClick}
    >
      <span>{productName}</span>
      <span>({campaignCount})</span>
    </button>
  );
};

const CampaignListView = ({
  selectedProduct,
  campaigns,
  firstCampaigns,
  onProductSelect,
  onCampaignDetails,
  onCampaignApply,
  getTotalCampaignCount,
  getCampaignsByProductType,
  currency,
}) => {
  const t = useTranslationDetector();

  return (
    <div className="flex flex-col">
      <div className="scrollbar-hide flex items-center justify-start gap-2 overflow-scroll px-5 py-4">
        <ProductFilter
          productType="All"
          campaignCount={getTotalCampaignCount()}
          isSelected={!selectedProduct}
          onClick={() => onProductSelect(null)}
        />
        {firstCampaigns.map((campaign) => (
          <ProductFilter
            key={campaign.CampaignId}
            productType={campaign.ProductType}
            campaignCount={getTotalCampaignCount(
              campaign.ProductType.toUpperCase(),
            )}
            isSelected={selectedProduct === campaign.ProductType}
            onClick={() => onProductSelect(campaign.ProductType)}
          />
        ))}
      </div>

      <div className="bg-background-global flex flex-col gap-4 px-5 py-6">
        <div className="font-semibold">
          {t(PRODUCT_TRANSLATIONS[selectedProduct] || PRODUCT_TRANSLATIONS.All)}{" "}
          ({getTotalCampaignCount(selectedProduct?.toUpperCase())})
        </div>
        <div className="scrollbar-promo max-h-[440px] overflow-y-auto pr-1">
          <div className="flex flex-col gap-4">
            {getCampaignsByProductType(selectedProduct?.toUpperCase()).map(
              (campaign) => (
                <CampaignListItem
                  key={campaign.CampaignId}
                  campaign={campaign}
                  onDetailsClick={() => onCampaignDetails(campaign)}
                  onApplyClick={() => onCampaignApply(campaign)}
                  currency={currency}
                />
              ),
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const OpportunitiesCardWrapper = () => {
  const uniqueId = useId();
  const modalId = `campaign-info-modal-${uniqueId}`;

  const [campaigns, setCampaigns] = useState({});
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const router = useRouter();
  const locale = useLocale();
  const t = useTranslationDetector();
  const campaignModal = useModal(modalId);
  const { currency } = useCurrency();

  // Fetch campaigns
  const getCampaignData = useCallback(async () => {
    try {
      const response = await commonService.getPersonalOffersCampaigns({
        LanguageCode: locale,
        IsPerfonalOffers: true,
      });
      if (response?.CampaignsByProductType) {
        setCampaigns(response.CampaignsByProductType);
      }
    } catch (error) {
      console.error(error);
    }
  }, [locale]);

  useEffect(() => {
    getCampaignData();
  }, [getCampaignData]);

  // Memoized helpers
  const firstCampaigns = useMemo(
    () =>
      Object.values(campaigns).flatMap((arr) =>
        Array.isArray(arr) && arr.length > 0 ? [arr[0]] : [],
      ),
    [campaigns],
  );

  const getCampaignsByProductType = useCallback(
    (productType) => {
      if (!productType) {
        return Object.values(campaigns)
          .filter((arr) => Array.isArray(arr))
          .flat();
      }
      return Array.isArray(campaigns[productType])
        ? campaigns[productType]
        : [];
    },
    [campaigns],
  );

  const getTotalCampaignCount = useCallback(
    (productType) => {
      if (!productType) {
        return Object.values(campaigns)
          .filter((arr) => Array.isArray(arr))
          .reduce((total, arr) => total + arr.length, 0);
      }
      const campaignsByProductType = campaigns[productType];
      return Array.isArray(campaignsByProductType)
        ? campaignsByProductType.length
        : 0;
    },
    [campaigns],
  );

  // Handlers
  const handleUseCampaign = useCallback(
    (campaign) => {
      // Kupon seçildiğinde dataLayer eventi gönder
      /*if (typeof window !== "undefined") {
        window.dataLayer = window.dataLayer || [];
        const couponName =
          campaign?.CampaignCode || campaign?.DiscountText || null;
        const couponValue = campaign?.DiscountText || null;
        const couponCurrency = campaign?.Currency || "TRY";

       
        window.dataLayer.push({
          event: "custom_event",
          event_category: "coupon",
          event_action: "select_coupon",
          coupon_name: couponName,
          coupon_value: couponValue,
          coupon_currency: couponCurrency,
        });
       
      } */

      const route = PRODUCT_ROUTES[campaign.ProductType] || "/bus-ticket";
      campaignModal.close();
      router.push(`${route}?campaignId=${campaign.CampaignId}`);
    },
    [campaignModal, router],
  );

  const handleCampaingApplyClick = useCallback((campaign) => {
    /*
    const couponName = campaign?.CampaignCode || campaign?.DiscountText || null;
    const couponValue = campaign?.DiscountText || null;
    const couponCurrency = campaign?.Currency || "TRY";

    sendGTMEvent({
      event: "custom_event",
      event_category: "coupon",
      event_action: DATA_LAYER_EVENT_NAMES.COUPON_APPLIED,
      coupon_name: couponName,
      coupon_value: couponValue,
      coupon_currency: couponCurrency,
    });
    */
  }, []);

  const showInfoModal = useCallback(
    (productType) => {
      setSelectedProduct(productType);
      campaignModal.open({
        isBottomSheet: true,
        contentClassName: "p-0",
        size: "md",
        title: t("common.opportunitiesTitle"),
      });
    },
    [campaignModal, t],
  );

  const showCampaignDetails = useCallback((campaign) => {
    // Kupon detayı görüntülendiğinde dataLayer eventi gönder
    /*if (typeof window !== "undefined") {
      window.dataLayer = window.dataLayer || [];
      const couponName =
        campaign?.CampaignCode || campaign?.DiscountText || null;
      const couponValue = campaign?.DiscountText || null;
      const couponCurrency = campaign?.Currency || "TRY";

      window.dataLayer.push({
        event: "custom_event",
        event_category: "coupon",
        event_action: "view_coupon",
        coupon_name: couponName,
        coupon_value: couponValue,
        coupon_currency: couponCurrency,
      });
      
    }*/

    setSelectedCampaign(campaign);
  }, []);

  const handleBackToList = useCallback(() => {
    setSelectedCampaign(null);
  }, []);

  // Kupon kartı tıklandığında dataLayer eventi gönder
  const handleCampaingCtaClick = useCallback((campaign) => {
    sendGTMEvent({
      event: "custom_event",
      event_category: "click",
      event_action: "cta_click",
      cta_button: campaign?.ProductType || null, //tıklanan kuponun ürün türü
      cta_location: "body", // sayfada bulunduğu konum
      cta_category: t(PRODUCT_TRANSLATIONS[campaign?.ProductType || null]), //başlığın ismi
      cta_language: locale?.toUpperCase() || null,
    });
  }, []);

  const handleAllCampaingCtaClick = useCallback(() => {
    sendGTMEvent({
      event: "custom_event",
      event_category: "click",
      event_action: "cta_click",
      cta_button: "view_all", //
      cta_location: "body", // sayfada bulunduğu konum
      cta_category: t(PRODUCT_TRANSLATIONS.All), //başlığın ismi
      cta_language: locale?.toUpperCase() || null,
    });
  }, []);

  // Early return if no campaigns
  if (Object.keys(campaigns).length === 0) return null;

  return (
    <>
      <div className="">
        <div className="bg-promo-gradient border-promo-subtle flex flex-col gap-4 rounded-lg border py-4 pl-4">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between pr-4">
              <h2 className="text-promo text-lg font-semibold md:text-[23px]">
                {t("common.opportunitiesTitle")}
              </h2>
              <button
                className="bg-promo cursor-pointer rounded-lg px-3 py-1.5 text-sm font-medium text-white md:px-4 md:py-2.5"
                onClick={() => {
                  showInfoModal(null);
                  handleAllCampaingCtaClick();
                }}
              >
                {t("common.allView")}
              </button>
            </div>
            <div className="scrollbar-hide flex h-full items-center justify-start gap-2 overflow-x-scroll overflow-y-hidden pr-4 md:gap-4">
              {firstCampaigns.map((campaign) => (
                <CampaignCard
                  key={campaign.CampaignId}
                  campaign={campaign}
                  onClick={() => {
                    showInfoModal(campaign.ProductType);
                    handleCampaingCtaClick(campaign);
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <Modal
        id={modalId}
        onClose={() => {
          campaignModal.close();
          setSelectedCampaign(null);
        }}
      >
        {selectedCampaign ? (
          <CampaignDetailsView
            campaign={selectedCampaign}
            onBack={handleBackToList}
            currency={currency}
          />
        ) : (
          <CampaignListView
            selectedProduct={selectedProduct}
            campaigns={campaigns}
            firstCampaigns={firstCampaigns}
            onProductSelect={setSelectedProduct}
            onCampaignDetails={showCampaignDetails}
            onCampaignApply={(campaign) => {
              handleUseCampaign(campaign);
              handleCampaingApplyClick(campaign);
            }}
            getTotalCampaignCount={getTotalCampaignCount}
            getCampaignsByProductType={getCampaignsByProductType}
            currency={currency}
          />
        )}
      </Modal>
    </>
  );
};

export default OpportunitiesCardWrapper;
